/**
 * moonshot-cart-error-persistence.js
 *
 * Warehouse's <buy-button> auto-dismisses its native error toast after
 * 5.5s, and the toast renders near the Add-to-Cart button — far from
 * where the customer's eye is once they look up. For our cart-validation
 * Function errors (mixed pickup stores, stream item with PFD pickup,
 * etc.) the customer needs:
 *   1. The message to STAY VISIBLE until they act on it.
 *   2. To NOT see two competing surfaces (native toast + our banner).
 *   3. The remaining surface to be loud enough they can't miss it.
 *
 * Approach (without touching theme.js — compiled Warehouse code):
 *   - MutationObserver watches for inserted `.cart__status-message`
 *     error nodes.
 *   - When one shows up: read its text, then HIDE the native
 *     `.alert--error` block so it doesn't render the bouncing toast.
 *   - Mirror the text into a fixed top-of-page banner (full-width,
 *     red, bold). Slide-in on entry; gentle shake to grab attention.
 *   - Auto-scroll to top so the customer is brought to the banner
 *     (helpful when they're scrolled mid-page).
 *
 * Loaded after theme.js via layout/theme.liquid.
 */
(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const BANNER_ID = "moonshot-persistent-cart-error";
  const STYLE_ID = "moonshot-cart-error-styles";
  const BODY_CLASS = "moonshot-cart-error-active";

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      @keyframes moonshot-cart-error-in {
        from { transform: translateY(-100%); opacity: 0; }
        to   { transform: translateY(0);     opacity: 1; }
      }
      @keyframes moonshot-cart-error-shake {
        0%, 100% { transform: translateX(0); }
        15%, 55% { transform: translateX(-8px); }
        35%, 75% { transform: translateX(8px); }
      }
      #${BANNER_ID} {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 100000;
        background: #d23a26;
        color: #ffffff;
        padding: 18px 60px 18px 24px;
        font-size: 1.05rem;
        font-weight: 600;
        line-height: 1.5;
        text-align: center;
        box-shadow: 0 4px 24px rgba(0,0,0,0.25);
        animation: moonshot-cart-error-in 0.3s ease-out;
      }
      #${BANNER_ID}.moonshot-cart-error--shake {
        animation: moonshot-cart-error-shake 0.55s ease-in-out;
      }
      #${BANNER_ID} button[data-moonshot-error-dismiss] {
        position: absolute;
        top: 12px;
        right: 14px;
        background: rgba(255,255,255,0.15);
        border: none;
        border-radius: 4px;
        color: #ffffff;
        cursor: pointer;
        font-size: 1.5rem;
        line-height: 1;
        padding: 4px 10px;
      }
      #${BANNER_ID} button[data-moonshot-error-dismiss]:hover {
        background: rgba(255,255,255,0.28);
      }
      /* Push body down so the fixed banner doesn't cover the header /
         nav — customer can still navigate around the error. */
      body.${BODY_CLASS} {
        padding-top: 64px;
      }
      /* Suppress Warehouse's native error toast for any cart error we
         mirror. We hide AFTER reading the text so the message survives
         in our banner; we just don't render the bouncing native toast. */
      .cart__status-message .alert--error.moonshot-suppressed {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  function ensureBanner() {
    let banner = document.getElementById(BANNER_ID);
    if (banner) return banner;
    banner = document.createElement("div");
    banner.id = BANNER_ID;
    banner.setAttribute("role", "alert");
    banner.setAttribute("aria-live", "assertive");
    banner.setAttribute("data-moonshot-cart-error-banner", "");

    const content = document.createElement("div");
    content.setAttribute("data-moonshot-cart-error-content", "");
    banner.appendChild(content);

    const dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.setAttribute("aria-label", "Dismiss");
    dismiss.setAttribute("data-moonshot-error-dismiss", "");
    dismiss.textContent = "×";
    dismiss.addEventListener("click", () => {
      banner.remove();
      document.body.classList.remove(BODY_CLASS);
    });
    banner.appendChild(dismiss);

    document.body.appendChild(banner);
    document.body.classList.add(BODY_CLASS);
    return banner;
  }

  function pinError(message) {
    const trimmed = String(message || "").trim();
    if (!trimmed) return;
    injectStyles();
    const banner = ensureBanner();
    const content = banner.querySelector("[data-moonshot-cart-error-content]");
    if (!content) return;
    content.textContent = trimmed;
    // Re-trigger the shake even on repeated errors so a customer clicking
    // Add-to-Cart twice gets re-emphasized feedback.
    banner.classList.remove("moonshot-cart-error--shake");
    void banner.offsetWidth; // force reflow so the animation re-runs
    banner.classList.add("moonshot-cart-error--shake");
    // Bring banner into view if customer is scrolled.
    if (window.scrollY > 80) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function suppressNative(alertWrapper) {
    if (!alertWrapper || !alertWrapper.classList) return;
    alertWrapper.classList.add("moonshot-suppressed");
  }

  function extractErrorFromNode(rootNode) {
    if (!rootNode || rootNode.nodeType !== 1) return null;
    let statusMsg = null;
    if (rootNode.matches && rootNode.matches(".cart__status-message")) {
      statusMsg = rootNode;
    } else if (rootNode.querySelector) {
      statusMsg = rootNode.querySelector(".cart__status-message");
    }
    if (!statusMsg) return null;
    const alertWrapper = statusMsg.querySelector(".alert--error");
    if (!alertWrapper) return null;
    const p = alertWrapper.querySelector("p");
    const text = ((p && p.textContent) || alertWrapper.textContent || "").trim();
    if (!text) return null;
    return { text, alertWrapper };
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => {
        const result = extractErrorFromNode(node);
        if (result) {
          pinError(result.text);
          suppressNative(result.alertWrapper);
        }
      });
    }
  });

  function start() {
    // Pre-inject styles so the suppress rule is live before any error
    // node is observed — avoids a first-paint flash of the native toast.
    injectStyles();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
